import { ChangeEvent, Dispatch, FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material/Select';

import { CustomButton, CustomCheckBox, CustomInput, MultipleSelect, Text } from '../../../shared/uiComponents';
import { AdminContainer as Container, InputWrapper } from '../../../components/StyledComponents';
import { ACTIONS, ActionProperties } from './insuranceReducer';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { CreateInsuranceProperties } from '../../../redux/API/ClientAPIHelpers/insuranceProperties';
import { getBranches } from '../../../redux/State/clientSlice/branchSlice';

interface InsuranceFormProperties {
	branchIds: string[];

	insuranceInfo: Partial<CreateInsuranceProperties>;
	onBranchChange: (branchIds: string[]) => void;
	saveInsuranceHandler: () => void;
	saving?: boolean;
	setInsuranceInfo: Dispatch<ActionProperties>;
}

interface ErrorProperties {
	ageLimit: string;

	name: string;
	timelyFilingForClaims: string;
}

const InsuranceForm: FC<InsuranceFormProperties> = ({
	insuranceInfo,
	setInsuranceInfo,
	saveInsuranceHandler,
	branchIds,
	onBranchChange,
	saving = false,
}) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { id } = useParams();

	const [error, setError] = useState<ErrorProperties>({
		name: '',
		timelyFilingForClaims: '',
		ageLimit: '',
	});

	const [disableSave, setDisableSave] = useState<boolean>(true);

	const branches = useSelector((state) => state.branch.branches);

	useEffect(() => {
		dispatch(getBranches());
	}, [dispatch]);

	useEffect(() => {
		const hasRequiredFields =
			insuranceInfo?.name &&
			insuranceInfo.name.trim().length > 0 &&
			(insuranceInfo.timelyFilingForClaims || 0) > 0 &&
			(insuranceInfo.ageLimit || 0) > 0;

		const hasErrors = Boolean(error.name) || Boolean(error.timelyFilingForClaims) || Boolean(error.ageLimit);

		setDisableSave(!hasRequiredFields || hasErrors);
	}, [insuranceInfo, error]);

	const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setInsuranceInfo({ type: ACTIONS.setName, payload: value });
		setError((prev) => ({ ...prev, name: '' }));
	};

	const onNameBlurHandler = () => {
		if (!insuranceInfo.name || insuranceInfo.name.trim().length === 0) {
			setError((prev) => ({ ...prev, name: 'Required' }));
		}
	};

	const onTimelyFilingChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = parseInt(event.target.value) || 0;
		setInsuranceInfo({ type: ACTIONS.setTimelyFiling, payload: value });
		setError((prev) => ({ ...prev, timelyFilingForClaims: '' }));
	};

	const onTimelyFilingBlur = () => {
		if (!insuranceInfo.timelyFilingForClaims || insuranceInfo.timelyFilingForClaims <= 0) {
			setError((prev) => ({
				...prev,
				timelyFilingForClaims: 'Must be greater than 0',
			}));
		}
	};

	const onAgeLimitChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = parseInt(event.target.value) || 0;
		setInsuranceInfo({ type: ACTIONS.setAgeLimit, payload: value });
		setError((prev) => ({ ...prev, ageLimit: '' }));
	};

	const onAgeLimitBlur = () => {
		if (!insuranceInfo.ageLimit || insuranceInfo.ageLimit <= 0) {
			setError((prev) => ({ ...prev, ageLimit: 'Must be greater than 0' }));
		}
	};

	const onMinBillableMinutesChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value ? parseInt(event.target.value) : undefined;
		setInsuranceInfo({ type: ACTIONS.setMinBillableMinutes, payload: value });
	};

	const onCheckboxChange = (field: string) => (checked: boolean) => {
		setInsuranceInfo({
			type: ACTIONS[field as keyof typeof ACTIONS],
			payload: checked,
		});
	};

	const handleBranchChange = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		onBranchChange(value);
	};

	return (
		<Container>
			<Text
				title={id ? 'Edit Insurance' : 'Add Insurance'}
				type="h4"
				font="header"
				fontWeight="semiBold"
				className="marginBottom16"
			/>

			{/* Basic Information */}
			<Text title="Basic Information" type="h6" fontWeight="semiBold" className="marginBottom16" />

			<InputWrapper>
				<CustomInput
					label="Insurance Name *"
					value={insuranceInfo.name || ''}
					onChange={onNameChange}
					onBlur={onNameBlurHandler}
					error={Boolean(error.name)}
					errorMessage={error.name}
					placeholder="Enter insurance name"
				/>
			</InputWrapper>

			<div
				style={{
					display: 'grid',
					gridTemplateColumns: '1fr 1fr 1fr',
					gap: '16px',
					marginBottom: '24px',
				}}
			>
				<CustomCheckBox
					item={{
						id: 'isActive',
						checked: insuranceInfo.isActive ?? true,
						label: 'Active',
					}}
					onChange={(event) => onCheckboxChange('setIsActive')(event.target.checked)}
				/>
				<CustomCheckBox
					item={{
						id: 'canBeAssigned',
						checked: insuranceInfo.canBeAssigned ?? true,
						label: 'Can Be Assigned',
					}}
					onChange={(event) => onCheckboxChange('setCanBeAssigned')(event.target.checked)}
				/>
				<CustomCheckBox
					item={{
						id: 'isOutOfNetwork',
						checked: insuranceInfo.isOutOfNetwork ?? false,
						label: 'Out of Network',
					}}
					onChange={(event) => onCheckboxChange('setIsOutOfNetwork')(event.target.checked)}
				/>
			</div>

			{/* Branch Assignment */}
			<Text title="Branch Assignment" type="h6" fontWeight="semiBold" className="marginBottom16" />

			<div style={{ maxWidth: '450px' }}>
				<MultipleSelect
					label="Branches"
					data={branches}
					setValue={handleBranchChange}
					initialValue={branchIds}
					backgroundType="base"
					className="marginBottom16"
				/>
				<Text
					title="Not setting a branch will assign this insurance to the default branch"
					type="pxs"
					color="#666"
					className="marginBottom16"
				/>
			</div>

			{/* Billing Configuration */}
			<Text title="Billing Configuration" type="h6" fontWeight="semiBold" className="marginBottom16" />

			<div
				style={{
					display: 'grid',
					gridTemplateColumns: '1fr 1fr',
					gap: '16px',
					marginBottom: '24px',
				}}
			>
				<CustomInput
					label="Timely Filing (Days) *"
					type="number"
					value={insuranceInfo.timelyFilingForClaims?.toString() || ''}
					onChange={onTimelyFilingChange}
					onBlur={onTimelyFilingBlur}
					error={Boolean(error.timelyFilingForClaims)}
					errorMessage={error.timelyFilingForClaims}
					placeholder="Enter days"
				/>
				<CustomInput
					label="Age Limit (Years) *"
					type="number"
					value={insuranceInfo.ageLimit?.toString() || ''}
					onChange={onAgeLimitChange}
					onBlur={onAgeLimitBlur}
					error={Boolean(error.ageLimit)}
					errorMessage={error.ageLimit}
					placeholder="Enter age limit"
				/>
			</div>

			<InputWrapper>
				<CustomInput
					label="Min Billable Minutes/Day"
					type="number"
					value={insuranceInfo.minimumBillableMinutesPerDay?.toString() || ''}
					onChange={onMinBillableMinutesChange}
					placeholder="Enter minimum minutes (optional)"
				/>
			</InputWrapper>

			{/* Business Rules */}
			<Text title="Business Rules" type="h6" fontWeight="semiBold" className="marginBottom16" />

			<div
				style={{
					display: 'grid',
					gridTemplateColumns: '1fr',
					gap: '12px',
					marginBottom: '24px',
				}}
			>
				<CustomCheckBox
					item={{
						id: 'allowedLbaWithoutBcba',
						checked: insuranceInfo.allowedLbaWithoutBcba ?? false,
						label: 'Allow LBA without BCBA',
					}}
					onChange={(event) => onCheckboxChange('setAllowedLbaWithoutBcba')(event.target.checked)}
				/>
				<CustomCheckBox
					item={{
						id: 'twoBcbaCanBillAtSameDay',
						checked: insuranceInfo.twoBcbaCanBillAtSameDay ?? false,
						label: 'Two BCBAs can bill same day',
					}}
					onChange={(event) => onCheckboxChange('setTwoBcbaCanBillAtSameDay')(event.target.checked)}
				/>
				<CustomCheckBox
					item={{
						id: 'limitedBcbasBillAsNotCredentialed',
						checked: insuranceInfo.limitedBcbasBillAsNotCredentialed ?? false,
						label: 'Limited BCBAs bill as not credentialed',
					}}
					onChange={(event) => onCheckboxChange('setLimitedBcbasBillAsNotCredentialed')(event.target.checked)}
				/>
				<CustomCheckBox
					item={{
						id: 'openAuth',
						checked: insuranceInfo.openAuth ?? false,
						label: 'Open Authorization',
					}}
					onChange={(event) => onCheckboxChange('setOpenAuth')(event.target.checked)}
				/>
			</div>

			{/* Telehealth Settings */}
			<Text title="Telehealth Settings" type="h6" fontWeight="semiBold" className="marginBottom16" />

			<div
				style={{
					display: 'grid',
					gridTemplateColumns: '1fr 1fr',
					gap: '16px',
					marginBottom: '32px',
				}}
			>
				<CustomCheckBox
					item={{
						id: 'allowTelehealthForAssessment',
						checked: insuranceInfo.allowTelehealthForAssessment ?? false,
						label: 'Allow for Assessment',
					}}
					onChange={(event) => onCheckboxChange('setAllowTelehealthForAssessment')(event.target.checked)}
				/>
				<CustomCheckBox
					item={{
						id: 'allowTelehealthForBTM',
						checked: insuranceInfo.allowTelehealthForBTM ?? false,
						label: 'Allow for BTM',
					}}
					onChange={(event) => onCheckboxChange('setAllowTelehealthForBTM')(event.target.checked)}
				/>
			</div>

			<CustomButton
				title={id ? 'Update Insurance' : 'Save Insurance'}
				onClick={saveInsuranceHandler}
				disabled={disableSave || saving}
				loading={saving}
				className="marginBottom16"
			/>
		</Container>
	);
};

export default InsuranceForm;
